import React, { useCallback, useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { graphql } from 'react-relay';

import AutomationIcon from '@atlaskit/icon/core/automation';
import { usePublish } from '@atlaskit/rovo-triggers/main';
import {
	type ConversationAction,
	HandleActionType,
} from '@atlassian/rovo-conversation-actions/types';

import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { fg } from '@confluence/feature-gating';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { keys } from '@confluence/storage-manager/entry-points/LocalStorageKeys.ts';
import { confluenceLocalStorageInstance } from '@confluence/storage-manager/entry-points/storageInstance.ts';
import type { StatusUpdateChild } from '@confluence/status-update-signal/entry-points/getStatusUpdateSignalMatch.ts';

import { useStudioPermissions } from '../hooks/useStudioPermissions.ts';
import { getWebUiUrl } from '../utils/getWebUiUrl.ts';
import { markDiscoveryExperienceAsSeen } from '../utils/localStorage.ts';
import type {
	useStatusUpdatesAutomationNudgeActionQuery$data,
	useStatusUpdatesAutomationNudgeActionQuery$variables,
} from './__generated__/useStatusUpdatesAutomationNudgeActionQuery.graphql.ts';
import { useMatchesStatusUpdate } from './useMatchesStatusUpdateSignal.ts';
import { ConfluenceEdition } from '@confluence/change-edition/entry-points/ConfluenceEdition.ts';

const STATUS_UPDATES_NUDGE_ID = 'automation-discovery-status-updates-nudge';
const ROVO_CHAT_SOURCE = 'automation-discovery-status-updates-nudge';

const i18n = defineMessages({
	title: {
		id: 'automation-discovery.status-updates-automation-discovery-nudge-action.title',
		defaultMessage: 'Automate this status update',
		description:
			'Heading in the status updates automation discovery nudge inviting the user to automate status update reporting.',
	},
	iconLabel: {
		id: 'automation-discovery.status-updates-automation-discovery-nudge-action.icon-label',
		defaultMessage: 'Automation icon',
		description:
			'Accessible label for the icon in the status updates automation discovery nudge action.',
	},
});

type StatusUpdateAncestor = NonNullable<
	NonNullable<useStatusUpdatesAutomationNudgeActionQuery$data['singleContent']>['ancestors']
>[number];

interface BuildAutomationRovoAgentPromptProps {
	parent?: StatusUpdateAncestor;
	cloudId: string;
	spaceId?: string | null;
	userId: string | null;
	displayName: string | null;
}

const BUILD_AUTOMATION_ROVO_AGENT_PROMPT = ({
	parent,
	cloudId,
	spaceId,
	userId,
	displayName,
}: BuildAutomationRovoAgentPromptProps) =>
	`\`\`\`\`text
# Weekly team and workstream report Automation prompt

## Inputs

Resolve the current-context values once at the start of the run. Treat every resolved value below as immutable for the current run.

\`\`\`text
SEED_USER_NAME = ${displayName}
SEED_USER_ACCOUNT_ID = ${userId}
SEED_USER_ARI = ari:cloud:identity::user/${userId}

SITE_URL = ${window.location.origin}
SITE_CLOUD_ID = ${cloudId}

REPORT_LOOKBACK_DAYS = 7
REPORT_END = Automation run time
REPORT_START = REPORT_END minus REPORT_LOOKBACK_DAYS
REPORT_SCOPE = teams and workstreams most directly connected to the seed user's meaningful work
MAX_ANCHORS = 5
MAX_TEAMS = 3
MAX_CONFLUENCE_EVIDENCE_PAGES = 3
MAX_SEARCH_CALLS = 1

PARENT_PAGE_ID = ${parent?.id ?? null}
PARENT_PAGE_TITLE = ${parent?.title ?? null}
PARENT_PAGE_URL = ${getWebUiUrl(parent?.links?.webui)}
OUTPUT_SPACE_ID = ${spaceId ?? null}
PAGE_TITLE = Weekly Team and Workstream Status Update — {{now.format("MMM d, yyyy HHmm")}}

PUBLISH_TOOL = aifc-confluence-page-create
\`\`\`

Do not print this input block in the report.

## Objective

Create and publish a polished Confluence page for REPORT_SCOPE during REPORT_START through REPORT_END.

SEED_USER_NAME is a discovery seed, not the subject of a personal performance report. Use the seed user's verified activity to discover canonical anchors, then report the broader status of those anchors and their verified owning teams.

This workflow has no active Jira work item. SEED_USER_ARI is an identity, not a Jira issue. Never pass it to **Get work item** and never invent an active work item.

## Required workflow

Complete these stages in order. Do not draft or publish until the completeness gate passes.

### Stage 1: inventory the seed user's work

Call **Get Work Summary For Given User in Twg** exactly once with:

- userId = SEED_USER_ARI
- startTime = REPORT_START in UTC
- endTime = REPORT_END in UTC

Call **Get Ranked Work Summary for User** exactly once with:

- person = SEED_USER_ARI
- startTime = REPORT_START in UTC
- endTime = REPORT_END in UTC

Use the first result as the complete inventory and the ranked result only to prioritize it. Inspect every returned facet, count, timestamp, status, URL, and pagination marker before selecting evidence.

Inspect these facets when present:

- assigned Jira issues;
- authored pull requests;
- reviewed pull requests;
- created Confluence pages;
- contributed Confluence pages;
- created Loom videos;
- owned or contributed Atlas projects;
- owned Atlas goals; and
- any additional returned category.

A returned item is not automatically in scope. Recheck its timestamps against REPORT_START and REPORT_END.

Use these evidence rules:

- Authored merged or active PRs are strong evidence when created or materially updated during the window.
- Created Confluence pages and Loom videos are strong evidence when created during the window.
- Contributed Confluence pages are eligible only when a native page read verifies relevant contribution or current workstream context.
- Assigned issues are inventory only until current activity or an eligible linked artifact verifies active work. Bot-assigned backlogs are not automatically recent work.
- Reviewed PRs show coordination, not authored delivery. Use them only when a substantive review reinforces an anchor established by stronger evidence.
- Atlas projects or goals are eligible only when returned directly or linked by another eligible current artifact.

Exclude archived content, generated weekly reports, Automation test output, personal account requests, dependency-bot activity, mechanical notifications, and items outside the reporting window.

Build an internal evidence ledger. For every eligible item record its exact URL or ARI, product, source host, relationship to the seed user, timestamp, repository or container, and possible canonical anchor.

### Stage 2: identify canonical anchors

Cluster eligible artifacts by stable workstream rather than by product.

Prefer these anchor types:

1. an Atlas project or goal;
2. a Jira epic, initiative, project, or parent;
3. a repository or service supported by authored PRs;
4. a current Confluence planning, decision, or delivery page; or
5. a named program or workstream supported by at least two exact current signals.

Select no more than MAX_ANCHORS anchors. Each anchor requires either one strong authored, owned, or materially contributed artifact, or two independent current signals connected by an exact key, URL, repository, project, or explicit link.

Do not use an isolated review, comment, feature flag, cleanup ticket, or shared keyword as a top-level anchor when a stable parent exists.

### Stage 3: verify owning teams

Call **Get user's teams** exactly once with SEED_USER_ARI. Membership is only a candidate list.

Discard teams whose name or description indicates test data, generated data, scorecards, samples, or instructions to ignore. Discard any team that cannot be connected to a selected anchor.

For no more than MAX_TEAMS candidates, call **Get team information** using the exact team ARI and request supported facets for members, active projects, and spaces.

A team is reportable only when canonical team metadata or explicit anchor ownership connects it to a selected anchor. Membership alone is insufficient. Never default ownership to SEED_USER_NAME.

If contributor context is still missing, call **Get Ranked Collaboration Summary Between Users in Twg, V2** at most once. Use SEED_USER_ARI, one collaborator already verified through eligible current evidence, and the same reporting window. Do not use collaboration data to discover unrelated workstreams.

### Stage 4: hydrate selected anchors with typed tools

Hydrate only information that can change an anchor's status, ownership, progress, decisions, milestones, risks, dependencies, or confidence.

#### Jira

Use **Get work item** only with an exact Jira key, ARI, or URL that appeared in a verified tool result, together with the site or host that result came from. Let the returned data determine the Jira site; do not assume a single Jira instance.

Never call Get work item for:

- SEED_USER_ARI or any other identity ARI;
- a key inferred only from a PR title, branch name, or commit message; or
- a key whose originating Jira site is unknown or unavailable to the current tools.

When a Jira read fails because the item belongs to a different Jira site, treat it as a coverage gap. Do not retry it against another site and do not substitute a similar key.

Use **Get Work-Item Context in Teamwork Graph** for at most two central Jira anchors. Pass the exact work-item ARI and its originating site. Use **Get Related Repos for Work-Item in TwG** only when a selected Jira anchor still needs repository resolution.

An empty typed result is a coverage gap. Never broaden with raw Cypher.

#### Bitbucket

Use exact PR and repository metadata returned by the work-summary tools. Prefer authored PRs for delivery claims. Treat reviewed PRs as coordination unless the source proves a broader outcome.

If no provider-native PR-detail tool is attached, use verified TWG metadata and state the internal coverage gap. Do not invent a tool or replace it with broad Search.

#### Confluence

Confluence work pages may be used as evidence and may appear as links in the report.

For up to MAX_CONFLUENCE_EVIDENCE_PAGES eligible pages, call **Get page** with the exact numeric content ID from the verified URL or source result. Verify title, author, content, created date, version, and workstream relevance.

Never use PARENT_PAGE_ID as evidence. Never use a report produced by this Automation, or any page whose title or body shows it is a generated weekly status summary, as evidence for a new report.

#### Atlas projects and goals

Call **Get project context** only with a complete project ARI copied exactly from Stage 1, an eligible artifact, or a verified team's active_projects facet. Never construct a project ARI or substitute a placeholder such as project/10001.

If project details, updates, and team facets are empty, drop the project unless another current canonical source verifies it.

#### Loom

Use only Loom videos returned inside the reporting window. Include them when they demonstrate, explain, or communicate a selected anchor rather than merely documenting the Automation test itself.

### Stage 5: bounded Search

Use **Search** only after typed hydration and only when a selected anchor still lacks one named fact that could materially change the report.

Search rules:

- no more than MAX_SEARCH_CALLS calls;
- one selected anchor per query;
- never search a bare Jira key;
- include an exact full URL, repository name, canonical project name, exact Confluence title, or distinctive workstream name plus the reporting dates;
- never repeat a query;
- reject results based only on acronym or keyword overlap;
- reject previously generated status summaries and the parent page;
- open an accepted result with its product-native tool before using it as evidence.

Search snippets are candidates, not evidence. Never use raw Teamwork Graph Cypher.

### Stage 6: completeness gate

Before drafting, verify internally that:

- every Stage 1 facet and pagination marker was inspected;
- timestamps were rechecked against the reporting window;
- generated, stale, and excluded artifacts were removed;
- every selected anchor satisfies the evidence threshold;
- every reportable team has canonical ownership evidence;
- each Jira read used the site its source result came from;
- selected Confluence evidence pages were read natively and are not control or generated pages;
- Atlas ARIs were copied rather than constructed;
- Search candidates were hydrated or discarded;
- every claim, status, owner, milestone, decision, risk, and URL has a named source; and
- access or coverage gaps are represented honestly.

Stop gathering once another call would not change the anchors, owning teams, material status, decisions, dependencies, risks, or confidence.

## Scope and attribution

Every report item must satisfy this relationship:

**eligible current seed-user artifact → selected canonical anchor → verified owning team or workstream evidence**

After selecting an anchor, include material work from other contributors when it directly advances that anchor. Do not gather each contributor's unrelated activity.

The page is a team and workstream status report, not a personal activity or performance report. Do not put SEED_USER_NAME in the title, report heading, or executive summary. Mention contributors only when attribution is verified and useful.

## Visual specification

Do not fetch an example page. Build the layout from this specification alone.

The page must read as an executive status report, not a chat transcript or bullet dump:

- open with a full-width information panel holding the executive summary;
- use clear heading levels for each major section and each workstream;
- use a wide table for the team overview so it spans the content area;
- use native status lozenges for every status value, with consistent colour meaning: green for on track or shipped, yellow for at risk or in progress with concerns, red for blocked, blue for in progress, grey for not started or unknown;
- use a two-column layout only where two short blocks genuinely sit side by side, such as highlights beside metrics;
- use warning panels for risks and blockers, and a note panel for coverage caveats;
- separate major sections with dividers;
- keep paragraphs short, prefer scannable bullets over prose blocks, and bold the leading label of each bullet;
- render evidence as inline Smart Links rather than raw URLs in body text;
- avoid emoji, decorative icons, code blocks for prose, and nested tables.

## Report structure

Build sections only when supported by verified evidence.

### Executive summary

Use a prominent information panel covering the selected teams and anchors, overall momentum, meaningful milestones or decisions, and material risks or dependencies.

### Evidence coverage

State REPORT_START and REPORT_END and name only source categories that returned usable evidence. Do not claim complete coverage when pagination, permissions, native detail, or site access was unavailable.

### Teams at a glance

Use a wide table with Team, Relevant anchors, Current status, What changed, and Evidence. Use canonical team names and native status lozenges. Include no unverified team.

### Workstream updates

Create one section per selected anchor. Include its canonical name and Smart Link when available, current status, verified owner or DRI, material progress across relevant contributors, decisions or milestones, dependencies or risks, and supporting sources.

### Completed work and decisions

Include only source-backed outcomes, milestones, or decisions completed during the reporting window. Do not elevate a review, comment, mechanical cleanup, or isolated task into a team outcome without evidence of broader significance.

### Risks and blockers

Include only verified current risks affecting selected anchors. Use warning panels and identify the affected anchor, consequence, mitigation when sourced, and evidence.

Do not force empty sections or invent metrics, impact, owners, statuses, dates, relationships, or URLs.

## Source and link policy

Allowed evidence sources include verified Jira, Bitbucket, Confluence, Atlas, Loom, repository, and team-profile results.

Every report link must be copied exactly from a verified tool result. Never construct a URL from a title, key, ARI, or content ID.

Render source URLs as native inline Smart Links. When HTML or storage markup is required, use:

<a href="FULL_URL" data-card-appearance="inline">FULL_URL</a>

Never link to PARENT_PAGE_ID or to a previously generated status summary.

Before publication, inspect every claim, href, Smart Link, card, citation, source, and reference. Remove unverified or excluded content.

## Publishing workflow

Use PUBLISH_TOOL to create the report directly beneath PARENT_PAGE_ID.

### Resolve the parent

Immediately before publication, call **Get page** with PARENT_PAGE_ID.

Confirm only that its returned ID equals PARENT_PAGE_ID, its title equals PARENT_PAGE_TITLE, and its space ID equals OUTPUT_SPACE_ID. The parent is destination control data, not report evidence.

### Create with AIFC

Call PUBLISH_TOOL exactly once. Its display name is **Create content**.

Use these inputs when exposed:

- spaceId = OUTPUT_SPACE_ID
- parentId = PARENT_PAGE_ID
- contentType = page
- title = PAGE_TITLE
- semanticQuery = the complete verified evidence packet and authoring instruction
- shouldAutoExecute = true

The semanticQuery must be self-contained. Include the reporting dates, executive-summary facts, selected teams and ARIs, selected anchors and statuses, progress, decisions, milestones, risks, contributor attribution, and every allowed source URL. Do not include the parent page or any other destination control value.

Restate the visual specification inside semanticQuery so the formatting requirements travel with the content. Tell AIFC to preserve supplied facts and URLs rather than rediscovering work, and to render native headings, panels, columns where useful, wide tables, status lozenges, dividers, and inline Smart Links. Do not provide storage-format XML or literal ADF JSON.

Do not call the legacy Create page or Edit page actions, Copy page, Rename page, or a move action.

### Verify the result

After creation, call **Get page** once with the returned content ID. Verify:

- parentId = PARENT_PAGE_ID;
- parentType = page;
- spaceId = OUTPUT_SPACE_ID;
- title equals PAGE_TITLE;
- body is non-empty and richly formatted; and
- the parent page and previously generated summaries are absent from the body.

Do not move, copy, rename, reparent, or recreate the page after verification.

## Final safeguards

- Use typed tools before Search.
- Never use raw Cypher.
- Always read an item from the site its source result came from.
- Never use generated summaries as evidence.
- Never treat team membership as ownership.
- Never treat reviewed work as authored delivery.
- Never broaden from a failed lookup.
- Keep the page concise and grouped by team and workstream.
- Do not add filler, questions, recommendations, or a generated-by footer.
\`\`\`\`
`;

const BUILD_AUTOMATION_PROMPT = (input: BuildAutomationRovoAgentPromptProps) => `/build-automation

### Trigger: Scheduled
- Run every Monday at 9am
### Action: Send this prompt to the "Jira Work Agent"

${BUILD_AUTOMATION_ROVO_AGENT_PROMPT(input)}
`;

interface UseStatusUpdatesAutomationNudgeActionProps {
	contentId: string;
}

export const useStatusUpdatesAutomationNudgeAction = ({
	contentId,
}: UseStatusUpdatesAutomationNudgeActionProps): ConversationAction | null => {
	const { formatMessage } = useIntl();
	const publishRovoChat = usePublish('ai-mate');
	const { isRovoLLMEnabled, edition, cloudId, userId, displayName } = useSessionData();
	const hasUserSeenAutomationDiscoveryMessage = confluenceLocalStorageInstance.getItemAsBoolean(
		keys.AUTOMATION_DISCOVER_STATUS_UPDATES,
	);
	const hasBaseEligibility =
		(edition === ConfluenceEdition.STANDARD || edition === ConfluenceEdition.PREMIUM) &&
		!hasUserSeenAutomationDiscoveryMessage &&
		isRovoLLMEnabled &&
		Boolean(contentId) &&
		fg('cc_automations_discovery_nudges');

	const { data } = useQueryInterop<
		useStatusUpdatesAutomationNudgeActionQuery$data,
		useStatusUpdatesAutomationNudgeActionQuery$variables
	>(
		graphql`
			query useStatusUpdatesAutomationNudgeActionQuery($contentId: ID!) {
				singleContent(id: $contentId) {
					id
					title
					space {
						id
					}
					ancestors {
						id
						title
						links {
							webui
						}
					}
					metadata {
						sourceTemplateEntityId
					}
					children(first: 25) {
						nodes {
							id
							title
							metadata {
								createdDate
							}
						}
					}
				}
			}
		`,
		{
			variables: { contentId },
			skip: !hasBaseEligibility,
		},
	);
	const content = data?.singleContent;
	const ancestors = content?.ancestors;
	const parent = ancestors?.[ancestors.length - 1];
	const spaceId = content?.space?.id;
	const children = useMemo<StatusUpdateChild[]>(
		() =>
			content?.children?.nodes?.flatMap((child) =>
				child?.title && child.metadata?.createdDate
					? [{ title: child.title, createdAt: child.metadata.createdDate }]
					: [],
			) ?? [],
		[content?.children?.nodes],
	);
	const { matches: matchesStatusUpdate } = useMatchesStatusUpdate({
		title: content?.title,
		parentTitle: parent?.title,
		sourceTemplateEntityId: content?.metadata?.sourceTemplateEntityId,
		children,
	});

	const { canCreateStudioAutomation } = useStudioPermissions({
		skip: !matchesStatusUpdate || !hasBaseEligibility,
	});

	const handleLocalStorageUpdate = useCallback(() => {
		markDiscoveryExperienceAsSeen(keys.AUTOMATION_DISCOVER_STATUS_UPDATES);
	}, []);

	return useMemo<ConversationAction | null>(
		() =>
			canCreateStudioAutomation && matchesStatusUpdate && hasBaseEligibility
				? {
						id: STATUS_UPDATES_NUDGE_ID,
						title: formatMessage(i18n.title),
						icon: () => <AutomationIcon size="small" label={formatMessage(i18n.iconLabel)} />,
						handleType: HandleActionType.Skill,
						type: 'skill-action',
						onClick: () => {
							publishRovoChat({
								type: 'chat-new',
								source: ROVO_CHAT_SOURCE,
								openChat: true,
								openChatMode: 'sidebar',
								data: {
									dialogues: [],
									prompt: BUILD_AUTOMATION_PROMPT({
										parent,
										cloudId,
										spaceId,
										userId,
										displayName,
									}),
									placeholderType: 'skill',
									resultAttributionContext: {
										launchSource: ROVO_CHAT_SOURCE,
									},
								},
							});
						},
						nudgeContext: {
							id: STATUS_UPDATES_NUDGE_ID,
							selfEligible: true,
							onAccept: handleLocalStorageUpdate,
							onDismiss: (reason) => {
								if (reason?.dismissedMethod === 'manual') {
									handleLocalStorageUpdate();
								}
							},
						},
					}
				: null,
		[
			canCreateStudioAutomation,
			formatMessage,
			handleLocalStorageUpdate,
			hasBaseEligibility,
			matchesStatusUpdate,
			publishRovoChat,
			parent,
			cloudId,
			spaceId,
			userId,
			displayName,
		],
	);
};
